import { useTu } from "@src/appV2/i18n/useTu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import {
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  type SxProps,
  type Theme,
} from "@mui/material";

import { mergeSxProps } from "@src/appV2/lib";
import { useState } from "react";
import { Text } from "../../components/Text";

interface MessageOverflowMenuProps {
  isIncoming: boolean;
  onMarkAsUnread?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * Small kebab trigger rendered next to the reply icon on message bubbles.
 * Opens a menu with one or more actions depending on which handlers are
 * provided. Outgoing messages typically get Edit + Delete; incoming get
 * Mark as unread. MUI's IconButton is used (rather than the custom
 * IconButton) because the inline context needs a chrome-less trigger that
 * sits next to the reply icon without a border.
 */
export function MessageOverflowMenu(props: MessageOverflowMenuProps) {
  const { tu } = useTu("chat");

  const { isIncoming, onMarkAsUnread, onEdit, onDelete, sx } = props;
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleMarkAsUnread = () => {
    setAnchorElement(undefined);
    onMarkAsUnread?.();
  };

  const handleEdit = () => {
    setAnchorElement(undefined);
    onEdit?.();
  };

  const handleDelete = () => {
    setAnchorElement(undefined);
    onDelete?.();
  };

  return (
    <>
      <MuiIconButton
        aria-label={tu("Message options")}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorElement)}
        size="small"
        sx={mergeSxProps(
          {
            padding: 0,
            flexShrink: 0,
            alignSelf: "center",
          },
          sx
        )}
        onClick={handleOpen}
      >
        <MoreHorizIcon
          sx={(theme) => ({
            fontSize: "1rem",
            color: isIncoming
              ? theme.palette.text.secondary
              : (theme.palette.base?.inverted?.muted ?? "rgba(255, 255, 255, 0.7)"),
          })}
        />
      </MuiIconButton>
      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        onClose={handleClose}
      >
        {onMarkAsUnread && (
          <MenuItem onClick={handleMarkAsUnread}>
            <Text variant="body2">{tu("Mark as unread")}</Text>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <Text variant="body2">{tu("Edit")}</Text>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <Text variant="body2">{tu("Delete")}</Text>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
